import { procedureEffectivePrice } from '../../utils/procedureMatching';

export const formatBudgetCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

export const procedureDisplayPrice = procedureEffectivePrice;

export const procedureDefaultSessions = (proc: any) => Math.max(
  1,
  Number(proc?.metadata?.ticket_default_sessions ?? proc?.default_sessions ?? proc?.estimated_sessions) || 1,
);

export const emptyBudgetItem = () => ({
  item_type: 'procedure' as 'procedure' | 'consultation',
  procedure_id: '',
  procedure_name: '',
  procedure_code: '',
  procedure_description: '',
  category_name: '',
  tooth_number: '',
  tooth_face: '',
  tooth_region: '',
  quantity: 1,
  unit_price: 0,
  discount_percentage: 0,
  notes: '',
  estimated_duration_minutes: 0,
  estimated_sessions: 1,
  requires_anesthesia: false,
  materials: [] as unknown[],
  sort_order: 0,
  group_name: '',
  insurance_id: '',
});

export const isConsultationItem = (item: any): boolean =>
  item?.item_type === 'consultation'
  || String(item?.category_name || '').toLowerCase() === 'consulta'
  || String(item?.procedure_name || '').toLowerCase() === 'consulta';

export const normalizeBudgetItem = (item: any) => ({
  ...emptyBudgetItem(),
  ...item,
  item_type: isConsultationItem(item) ? 'consultation' as const : 'procedure' as const,
});

export const defaultBudgetForm = () => ({
  discount_type: 'percentage' as 'percentage' | 'fixed',
  discount_value: 0,
  additional_charges: 0,
  payment_method: '',
  payment_conditions: '',
  installments: 1,
  entry_value: 0,
  validity_days: 30,
  clinical_indication: '',
  diagnosis: '',
  diagnosis_code: '',
  notes: '',
  internal_notes: '',
  dental_arch: '',
  show_unit_prices: true,
  show_procedure_codes: true,
  show_tooth_info: true,
  show_professional_info: true,
  show_payment_conditions: true,
  show_terms: true,
  show_warranty: false,
});

export type BudgetFormState = ReturnType<typeof defaultBudgetForm>;

export const calculateBudgetItemTotal = (item: any) => {
  const sub = (item.quantity || 1) * (item.unit_price || 0);
  return sub - sub * ((item.discount_percentage || 0) / 100);
};

export const itemFromCatalogProcedure = (proc: any, fallbackName: string, fallbackCode?: string) => ({
  ...emptyBudgetItem(),
  item_type: 'procedure' as const,
  procedure_id: proc?.id || '',
  procedure_name: proc?.name || fallbackName,
  procedure_code: proc?.code || fallbackCode || '',
  procedure_description: proc?.description || '',
  category_name: proc?.category?.name || proc?.clinic_procedure_categories?.name || '',
  unit_price: proc ? procedureDisplayPrice(proc) : 0,
  estimated_duration_minutes: proc?.duration_minutes || 0,
  estimated_sessions: procedureDefaultSessions(proc),
  requires_anesthesia: proc?.requires_anesthesia || false,
  insurance_id: proc?.insurance_id || '',
  quantity: 1,
});

export const isBudgetItemValid = (item: any) => {
  if (isConsultationItem(item)) return (Number(item.unit_price) || 0) > 0;
  return !!String(item.procedure_name || '').trim();
};

export const budgetFormFromTemplate = (template: any): BudgetFormState => {
  const meta = template?.metadata?.budget_form;
  if (meta && typeof meta === 'object') {
    return { ...defaultBudgetForm(), ...meta };
  }
  return {
    ...defaultBudgetForm(),
    discount_type: 'percentage',
    discount_value: Number(template?.default_discount_percentage) || 0,
    payment_conditions: template?.default_payment_conditions || '',
    validity_days: Number(template?.default_validity_days) || 30,
    notes: template?.default_notes || '',
  };
};

export const serializeBudgetTemplateItems = (items: any[]) =>
  items
    .filter(isBudgetItemValid)
    .map((it, idx) => ({
      item_type: isConsultationItem(it) ? 'consultation' : 'procedure',
      procedure_id: it.procedure_id || '',
      procedure_name: it.procedure_name,
      procedure_code: it.procedure_code || '',
      procedure_description: it.procedure_description || '',
      category_name: it.category_name || '',
      tooth_number: it.tooth_number || '',
      tooth_face: it.tooth_face || '',
      tooth_region: it.tooth_region || '',
      quantity: Math.max(1, Number(it.quantity) || 1),
      unit_price: Number(it.unit_price) || 0,
      discount_percentage: Number(it.discount_percentage) || 0,
      estimated_duration_minutes: Number(it.estimated_duration_minutes) || 0,
      estimated_sessions: Math.max(1, Number(it.estimated_sessions) || 1),
      requires_anesthesia: !!it.requires_anesthesia,
      materials: it.materials || [],
      group_name: it.group_name || '',
      notes: it.notes || '',
      sort_order: idx,
    }));

export const buildBudgetTemplatePayload = (
  meta: { name: string; description: string; specialty: string; category: string },
  budgetForm: BudgetFormState,
  items: any[],
) => ({
  name: meta.name.trim(),
  description: meta.description || '',
  specialty: meta.specialty || '',
  category: meta.category || '',
  items: serializeBudgetTemplateItems(items),
  default_discount_percentage: budgetForm.discount_type === 'percentage'
    ? Number(budgetForm.discount_value) || 0
    : 0,
  default_payment_conditions: budgetForm.payment_conditions || '',
  default_validity_days: Number(budgetForm.validity_days) || 30,
  default_notes: budgetForm.notes || '',
  metadata: { budget_form: budgetForm },
  is_active: true,
});
